import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  createQuestions,
  createSavedImportMapping,
  dryrunQuestionCreation,
  getQuestionFieldMappingForTempFile,
  listSavedImportMappings,
  uploadTempFile,
} from "@/hera-api/client/sdk.gen";
import { spreadsheetLineNumberFromImportError } from "@/lib/export-errors";
import { QUESTION_FIELD_GROUPS, getFieldLabel } from "@/lib/field-mappings";
import { applySavedFieldMappingToSheetInfo } from "@/lib/import-saved-mapping";
import { getApiErrorMessage } from "@/lib/utils";
import { Loader2, Upload as UploadIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ImportErrorsExportMenu } from "./import-errors-export-menu";

export type QuestionUploadType =
  | "registration"
  | "inspection"
  | "deregistration";

const QUESTION_TYPE_LABELS: Record<QuestionUploadType, string> = {
  registration: "Registration",
  inspection: "Inspection",
  deregistration: "De-registration",
};

interface QuestionUploadSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Determines which question type is created and the form title. */
  questionType?: QuestionUploadType;
  restrictToInspectionScopes?: boolean;
  restrictToDeregistrationScopes?: boolean;
}

export function QuestionUploadSheet({
  isOpen,
  onOpenChange,
  questionType = "registration",
  restrictToInspectionScopes: _restrictToInspectionScopes = false,
  restrictToDeregistrationScopes: _restrictToDeregistrationScopes = false,
}: QuestionUploadSheetProps) {
  const typeLabel = QUESTION_TYPE_LABELS[questionType];
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadToken, setUploadToken] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  // Sheet info state
  const [sheetInfo, setSheetInfo] = useState<Record<
    string,
    {
      headers: string[];
      row_count: number;
      mapping_warnings?: Array<{
        warning_type: string;
        field_key: string;
        mapped_value: string;
        message: string;
      }>;
    }
  > | null>(null);
  const [sheetInfoError, setSheetInfoError] = useState<string | null>(null);
  const [isSheetInfoLoading, setIsSheetInfoLoading] = useState(false);

  // Field mappings state
  const [fieldMappings, setFieldMappings] = useState<
    Record<string, Record<string, string>>
  >({});

  // Test results state
  const [testResults, setTestResults] = useState<Record<string, any | null>>(
    {},
  );
  const [testLoading, setTestLoading] = useState<Record<string, boolean>>({});
  const [testError, setTestError] = useState<Record<string, string | null>>({});

  // Creation state
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null);
  const [savedMappings, setSavedMappings] = useState<
    Array<{ id: string; name: string; field_mappings: Record<string, string> }>
  >([]);
  const [selectedSavedMappingId, setSelectedSavedMappingId] =
    useState<string>("");
  const [savedMappingsLoading, setSavedMappingsLoading] = useState(false);
  const structuralSheetInfoRef = useRef<Record<string, unknown> | null>(null);
  const firstMappingSourceRef = useRef<"infer" | "saved" | null>(null);

  // Set selectedSheet when sheetInfo is loaded
  useEffect(() => {
    if (sheetInfo && Object.keys(sheetInfo).length > 0 && !selectedSheet) {
      setSelectedSheet(Object.keys(sheetInfo)[0]);
    }
  }, [sheetInfo, selectedSheet]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files[0];
    if (
      (file &&
        [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "text/csv",
          "application/vnd.ms-excel",
        ].includes(file.type)) ||
      file.name.endsWith(".csv") ||
      file.name.endsWith(".xlsx")
    ) {
      setSelectedFile(file);
      setUploadToken(null);
    } else {
      toast({
        title: "Invalid file type",
        description: "Please upload a .csv or .xlsx file.",
        variant: "destructive",
      });
    }
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setSelectedFile(file);
        setUploadToken(null);
      }
    },
    [],
  );

  // Reset state when sheet closes
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setSelectedFile(null);
        setUploadToken(null);
        setSheetInfo(null);
        setSheetInfoError(null);
        setIsSheetInfoLoading(false);
        setFieldMappings({});
        setTestResults({});
        setTestLoading({});
        setTestError({});
        setSelectedSheet(null);
        setIsCreating(false);
        setCreateError(null);
        structuralSheetInfoRef.current = null;
        firstMappingSourceRef.current = null;
        setSavedMappings([]);
        setSelectedSavedMappingId("");
      }
      onOpenChange(open);
    },
    [onOpenChange],
  );

  const currentUploadType =
    questionType === "inspection"
      ? "questions_inspection"
      : questionType === "deregistration"
        ? "questions_deregistration"
        : "questions_registration";

  const applyMappingResponseToState = (mappingData: Record<string, any>) => {
    setSheetInfo(mappingData);
    const newMappings: Record<string, Record<string, string>> = {};
    for (const [sheetName, info] of Object.entries(mappingData)) {
      if ((info as any)?.field_mapping) {
        newMappings[sheetName] = { ...(info as any).field_mapping };
      }
    }
    setFieldMappings(newMappings);
  };

  const fetchSheetMappings = async (token: string, savedMappingId?: string) => {
    const infoResp = await getQuestionFieldMappingForTempFile({
      path: { token },
      query: {
        question_type: questionType,
        ...(savedMappingId ? { saved_mapping_id: savedMappingId } : {}),
      },
    });
    return ((infoResp as any).data ?? infoResp) as Record<string, any>;
  };

  const fetchSavedMappings = useCallback(async () => {
    setSavedMappingsLoading(true);
    try {
      const resp = await listSavedImportMappings({
        query: { upload_type: currentUploadType as any },
      });
      const data = ((resp as any).data ?? resp) as { mappings?: Array<any> };
      setSavedMappings(
        (data.mappings ?? []).map((m) => ({
          id: String(m.id),
          name: m.name,
          field_mappings: (m.field_mappings ?? {}) as Record<string, string>,
        })),
      );
    } catch {
      setSavedMappings([]);
    } finally {
      setSavedMappingsLoading(false);
    }
  }, [currentUploadType]);

  useEffect(() => {
    if (isOpen) {
      void fetchSavedMappings();
    }
  }, [isOpen, fetchSavedMappings]);

  const handleSavedMappingSelectChange = async (nextId: string) => {
    setSelectedSavedMappingId(nextId);
    if (!uploadToken) return;

    if (!nextId) {
      if (
        firstMappingSourceRef.current === "infer" &&
        structuralSheetInfoRef.current
      ) {
        applyMappingResponseToState(
          structuredClone(structuralSheetInfoRef.current) as Record<
            string,
            any
          >,
        );
        return;
      }
      setIsSheetInfoLoading(true);
      try {
        const mappingData = await fetchSheetMappings(uploadToken);
        applyMappingResponseToState(mappingData);
        structuralSheetInfoRef.current = structuredClone(mappingData);
        firstMappingSourceRef.current = "infer";
      } catch (err: any) {
        toast({
          title: "Failed to load column mapping",
          description: getApiErrorMessage(err, "Could not infer mapping."),
          variant: "destructive",
        });
      } finally {
        setIsSheetInfoLoading(false);
      }
      return;
    }

    const saved = savedMappings.find((m) => m.id === nextId);
    if (
      firstMappingSourceRef.current === "infer" &&
      structuralSheetInfoRef.current &&
      saved?.field_mappings
    ) {
      const applied = applySavedFieldMappingToSheetInfo(
        structuralSheetInfoRef.current as Record<string, any>,
        saved.field_mappings,
      );
      applyMappingResponseToState(applied as Record<string, any>);
      return;
    }

    setIsSheetInfoLoading(true);
    try {
      const mappingData = await fetchSheetMappings(uploadToken, nextId);
      applyMappingResponseToState(mappingData);
    } catch (err: any) {
      toast({
        title: "Failed to apply saved mapping",
        description: getApiErrorMessage(err, "Could not apply mapping."),
        variant: "destructive",
      });
    } finally {
      setIsSheetInfoLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Upload {typeLabel} Questions</SheetTitle>
          <SheetDescription>
            Upload a CSV or XLSX file to import {typeLabel.toLowerCase()}{" "}
            questions. All questions in the file will be created as{" "}
            {typeLabel.toLowerCase()} questions.
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-6 pl-4 pr-6 py-6 overflow-y-auto max-h-[calc(100vh-120px)]">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="min-w-[260px] flex-1">
              <div className="text-xs font-medium mb-1">Saved mapping</div>
              <select
                className="w-full border rounded px-2 py-1 text-xs"
                value={selectedSavedMappingId}
                onChange={(e) =>
                  void handleSavedMappingSelectChange(e.target.value)
                }
              >
                <option value="">
                  {savedMappingsLoading
                    ? "Loading..."
                    : "(No saved mapping selected)"}
                </option>
                {savedMappings.map((saved) => (
                  <option key={saved.id} value={saved.id}>
                    {saved.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!uploadToken || !selectedSheet}
              onClick={async () => {
                if (!selectedSheet) return;
                const name = window.prompt("Name this mapping:");
                if (!name || !name.trim()) return;
                try {
                  await createSavedImportMapping({
                    body: {
                      name: name.trim(),
                      upload_type: currentUploadType as any,
                      field_mappings: fieldMappings[selectedSheet] || {},
                    },
                  });
                  await fetchSavedMappings();
                  toast({
                    title: "Saved mapping created",
                    description: name.trim(),
                    variant: "secondary",
                  });
                } catch (err: any) {
                  toast({
                    title: "Failed to save mapping",
                    description: getApiErrorMessage(
                      err,
                      "Could not save mapping.",
                    ),
                    variant: "destructive",
                  });
                }
              }}
            >
              Save mapping
            </Button>
          </div>

          {/* Drag-and-drop/file picker */}
          {!selectedFile && (
            <div
              className={`border-2 border-dashed rounded-md p-6 text-center transition-colors ${
                isDragActive ? "border-primary bg-primary/10" : "border-muted"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center gap-2">
                <UploadIcon className="h-8 w-8 text-muted-foreground mb-2" />
                <span className="font-medium">
                  Drag and drop a CSV or XLSX file here
                </span>
                <span className="text-xs text-muted-foreground">or</span>
                <label
                  htmlFor="file-upload"
                  className="inline-block cursor-pointer"
                >
                  <input
                    id="file-upload"
                    type="file"
                    accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.xlsx"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  <span className="underline text-primary">Choose file</span>
                </label>
              </div>
            </div>
          )}

          {/* Selected file info */}
          {selectedFile && (
            <div className="border rounded-md p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UploadIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{selectedFile.name}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedFile(null);
                    setUploadToken(null);
                    setSheetInfo(null);
                    setSheetInfoError(null);
                    setIsSheetInfoLoading(false);
                    setFieldMappings({});
                    setTestResults({});
                    setTestLoading({});
                    setTestError({});
                    structuralSheetInfoRef.current = null;
                    firstMappingSourceRef.current = null;
                  }}
                >
                  Remove
                </Button>
              </div>
            </div>
          )}

          {/* Upload button */}
          {selectedFile && !uploadToken && (
            <Button
              onClick={async () => {
                if (!selectedFile) return;
                setIsUploading(true);
                setSheetInfo(null);
                setSheetInfoError(null);
                try {
                  const resp = await uploadTempFile({
                    body: { file: selectedFile },
                  });
                  const token = (resp as any).data?.token as string | undefined;
                  setUploadToken(token || null);
                  if (token) {
                    setIsSheetInfoLoading(true);
                    try {
                      const mappingData = await fetchSheetMappings(
                        token,
                        selectedSavedMappingId || undefined,
                      );
                      applyMappingResponseToState(mappingData);
                      structuralSheetInfoRef.current =
                        structuredClone(mappingData);
                      firstMappingSourceRef.current = selectedSavedMappingId
                        ? "saved"
                        : "infer";
                      setSheetInfoError(null);
                      await fetchSavedMappings();
                    } catch (err: any) {
                      setSheetInfoError(
                        getApiErrorMessage(err, "Failed to fetch sheet info"),
                      );
                      setSheetInfo(null);
                    } finally {
                      setIsSheetInfoLoading(false);
                    }
                  }
                  toast({
                    title: "Upload successful",
                    description: "File uploaded and headers loaded.",
                    variant: "secondary",
                  });
                } catch (err: any) {
                  toast({
                    title: "Upload failed",
                    description: getApiErrorMessage(
                      err,
                      "Could not upload file.",
                    ),
                    variant: "destructive",
                  });
                } finally {
                  setIsUploading(false);
                }
              }}
              disabled={isUploading}
              className="w-full"
            >
              {isUploading ? (
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
              ) : (
                <UploadIcon className="h-4 w-4 mr-2" />
              )}
              Upload
            </Button>
          )}

          {/* Sheet info loading */}
          {isSheetInfoLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading sheet info...</span>
              </div>
            </div>
          )}

          {/* Sheet info error */}
          {sheetInfoError && (
            <div className="text-destructive text-sm">{sheetInfoError}</div>
          )}

          {/* Sheet selection and field mappings */}
          {sheetInfo && !isSheetInfoLoading && (
            <Tabs
              defaultValue={Object.keys(sheetInfo)[0]}
              className="mt-6"
              onValueChange={setSelectedSheet}
            >
              <TabsList>
                {Object.keys(sheetInfo).map((sheetName) => (
                  <TabsTrigger
                    key={sheetName}
                    value={sheetName}
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-bold data-[state=active]:border-b-4 data-[state=active]:border-primary data-[state=active]:shadow-lg"
                  >
                    {sheetName}
                  </TabsTrigger>
                ))}
              </TabsList>
              {Object.entries(sheetInfo).map(([sheetName, info]) => (
                <TabsContent key={sheetName} value={sheetName}>
                  <div className="mb-4">
                    <div className="font-semibold mb-2">Column Mapping</div>
                    {(info.mapping_warnings?.length ?? 0) > 0 ? (
                      <div className="mb-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900">
                        {info.mapping_warnings?.map((warning, warningIdx) => (
                          <div
                            key={`${sheetName}-${warning.field_key}-${warningIdx}`}
                          >
                            {warning.message}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {QUESTION_FIELD_GROUPS.map((group) => (
                      <div key={group.title} className="mb-2">
                        <div className="font-semibold text-xs mb-1 truncate">
                          {group.title}
                        </div>
                        <div className="flex gap-2 px-2">
                          {[0, 1].map((col) => (
                            <div
                              key={col}
                              className="flex-1 flex flex-col gap-2"
                            >
                              {group.fields
                                .filter((f) => f.column === col)
                                .map((field) => (
                                  <div
                                    key={field.key}
                                    className="flex items-center gap-2"
                                  >
                                    <label
                                      htmlFor={`select-${field.key}`}
                                      className="w-32 text-right text-xs text-muted-foreground leading-tight"
                                    >
                                      {field.label}
                                    </label>
                                    <select
                                      id={`select-${field.key}`}
                                      className="border rounded px-2 py-1 text-xs"
                                      value={
                                        fieldMappings[sheetName]?.[field.key] ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        setFieldMappings((prev) => ({
                                          ...prev,
                                          [sheetName]: {
                                            ...prev[sheetName],
                                            [field.key]: e.target.value,
                                          },
                                        }))
                                      }
                                    >
                                      <option value="">(Not mapped)</option>
                                      {info.headers.map((header) => (
                                        <option key={header} value={header}>
                                          {header}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="pt-4">
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        disabled={
                          !fieldMappings[sheetName]?.question ||
                          testLoading[sheetName]
                        }
                        onClick={async () => {
                          setTestLoading((prev) => ({
                            ...prev,
                            [sheetName]: true,
                          }));
                          setTestError((prev) => ({
                            ...prev,
                            [sheetName]: null,
                          }));
                          setTestResults((prev) => ({
                            ...prev,
                            [sheetName]: null,
                          }));
                          if (!uploadToken) {
                            toast({
                              title: "No upload token",
                              description: "Please select a file first.",
                              variant: "destructive",
                            });
                            return;
                          }
                          try {
                            const resp = await dryrunQuestionCreation({
                              body: {
                                token: uploadToken,
                                sheet_name: sheetName,
                                field_mappings: fieldMappings[sheetName] || {},
                                question_type: questionType,
                              },
                            });
                            const data = (resp as any).data ?? resp;
                            setTestResults((prev) => ({
                              ...prev,
                              [sheetName]: data,
                            }));
                            toast({
                              title: "Test processing complete",
                              description: `Successfully processed: ${data.success_count}, Errors: ${data.error_count}`,
                              variant: "secondary",
                            });
                          } catch (err: any) {
                            setTestError((prev) => ({
                              ...prev,
                              [sheetName]: getApiErrorMessage(
                                err,
                                "Test failed",
                              ),
                            }));
                            toast({
                              title: "Test creation failed",
                              description: getApiErrorMessage(
                                err,
                                "Could not test question creation.",
                              ),
                              variant: "destructive",
                            });
                          } finally {
                            setTestLoading((prev) => ({
                              ...prev,
                              [sheetName]: false,
                            }));
                          }
                        }}
                      >
                        {testLoading[sheetName] ? (
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        ) : null}
                        Test Question Creation
                      </Button>
                    </div>
                  </div>
                  {/* Show test results */}
                  {testError[sheetName] && (
                    <div className="text-destructive text-sm mt-4">
                      {String(testError[sheetName])}
                    </div>
                  )}
                  {testResults[sheetName] && (
                    <div className="mt-4">
                      <div className="font-semibold mb-2">Sample Data</div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs border">
                          <thead>
                            <tr>
                              {testResults[sheetName]?.sample_data?.length >
                                0 &&
                                Object.keys(
                                  testResults[sheetName].sample_data[0],
                                ).map((colKey) => (
                                  <th
                                    key={colKey}
                                    className="border px-2 py-1 bg-muted text-muted-foreground"
                                  >
                                    {getFieldLabel(colKey)}
                                  </th>
                                ))}
                            </tr>
                          </thead>
                          <tbody>
                            {testResults[sheetName]?.sample_data?.map(
                              (row: any, i: number) => (
                                <tr key={String(row.id || row.name || i)}>
                                  {Object.entries(
                                    row as Record<string, unknown>,
                                  ).map(([colKey, val]) => (
                                    <td
                                      key={String(colKey)}
                                      className="border px-2 py-1"
                                    >
                                      {val === null || val === undefined
                                        ? ""
                                        : String(val)}
                                    </td>
                                  ))}
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                      {testResults[sheetName]?.errors?.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                            <div className="font-semibold text-destructive">
                              Errors
                            </div>
                            <ImportErrorsExportMenu
                              errors={testResults[sheetName]?.errors ?? []}
                              filenamePrefix="question-dry-run-errors"
                              uploadVariant
                              visibleStrategy={{ kind: "simple" }}
                            />
                          </div>
                          <div className="max-h-40 overflow-y-auto space-y-2">
                            {testResults[sheetName].errors.map(
                              (error: any, i: number) => (
                                <div
                                  key={`error-${i}-${spreadsheetLineNumberFromImportError(error as Record<string, unknown>, i)}`}
                                  className="text-xs text-destructive bg-destructive/10 p-2 rounded"
                                >
                                  <div className="font-medium">
                                    Row{" "}
                                    {spreadsheetLineNumberFromImportError(
                                      error as Record<string, unknown>,
                                      i,
                                    )}
                                    :
                                  </div>
                                  {Object.entries(error.errors).map(
                                    ([field, message]) => (
                                      <div key={field} className="ml-2">
                                        {field}: {String(message)}
                                      </div>
                                    ),
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
        <SheetFooter className="flex flex-row gap-2 justify-between">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={
              !uploadToken ||
              isCreating ||
              !selectedSheet ||
              !testResults[selectedSheet]?.sample_data?.length
            }
            onClick={async () => {
              setIsCreating(true);
              setCreateError(null);
              if (!uploadToken) {
                toast({
                  title: "No upload token",
                  description: "Please select a file first.",
                  variant: "destructive",
                });
                return;
              }
              try {
                // Use the selected sheet (or first sheet as fallback)
                const sheetName =
                  selectedSheet || Object.keys(sheetInfo ?? {})[0];
                const resp = await createQuestions({
                  body: {
                    token: uploadToken,
                    sheet_name: sheetName,
                    field_mappings: fieldMappings[sheetName] || {},
                    question_type: questionType,
                  },
                });
                const data = (resp as any).data ?? resp;
                toast({
                  title: "Questions created successfully",
                  description: `Successfully created: ${data.success_count}, Errors: ${data.error_count}`,
                  variant: "secondary",
                });
                handleOpenChange(false);
                // Optionally, refresh the page
                window.location.reload();
              } catch (err: any) {
                setCreateError(
                  getApiErrorMessage(err, "Failed to create questions"),
                );
                toast({
                  title: "Create failed",
                  description: getApiErrorMessage(
                    err,
                    "Could not create questions.",
                  ),
                  variant: "destructive",
                });
              } finally {
                setIsCreating(false);
              }
            }}
          >
            {isCreating ? (
              <Loader2 className="animate-spin h-4 w-4 mr-2" />
            ) : null}
            Create Questions
          </Button>
        </SheetFooter>
        {createError && (
          <div className="text-destructive text-xs mt-2">{createError}</div>
        )}
      </SheetContent>
    </Sheet>
  );
}
